import React, { useEffect, useState } from "react";
import { ZoomIn } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "./ui/carousel";
import ProjectImagesLightbox from "./ProjectImagesLightbox";

export const MAX_HOME_PROJECT_IMAGES = 10;
const AUTOPLAY_MS = 5000;

export const projectImageCardClass =
  "relative aspect-[4/5] w-full bg-white";

export const projectImageCardCompactClass =
  "relative aspect-[4/5] w-full max-h-56 bg-white sm:max-h-60 md:max-h-72 lg:max-h-80";

export const projectImageSkeletonClass =
  `${projectImageCardClass} animate-pulse rounded-2xl bg-slate-100`;

export const projectImageSkeletonCompactClass =
  `${projectImageCardCompactClass} animate-pulse rounded-2xl bg-slate-100`;

const prevClassName =
  "left-2 md:left-4 top-1/2 -translate-y-1/2 z-20 h-10 w-10 rounded-full border-slate-200 bg-white/95 text-[#0a1a2f] shadow-lg hover:bg-[#1e6fcf] hover:text-white hover:border-[#1e6fcf] disabled:opacity-40";

const nextClassName =
  "right-2 md:right-4 top-1/2 -translate-y-1/2 z-20 h-10 w-10 rounded-full border-slate-200 bg-white/95 text-[#0a1a2f] shadow-lg hover:bg-[#1e6fcf] hover:text-white hover:border-[#1e6fcf] disabled:opacity-40";

function ProjectCarouselSlide({ src, index, onZoom, enableLightbox, compact }) {
  const cardClass = compact ? projectImageCardCompactClass : projectImageCardClass;

  return (
    <article
      className={`group overflow-hidden border border-slate-200/80 bg-white shadow-sm transition-all duration-300 hover:border-[#1e6fcf]/40 hover:shadow-lg rounded-2xl`}
    >
      <div className={cardClass}>
        <img
          src={src}
          alt={`SSA Techno project showcase ${index + 1}`}
          className="absolute inset-0 h-full w-full bg-white object-contain transition-transform duration-500 group-hover:scale-[1.02]"
          loading={index < 2 ? "eager" : "lazy"}
          decoding="async"
        />

        {enableLightbox && (
          <button
            type="button"
            onClick={() => onZoom(index)}
            className="absolute inset-0 z-10 flex cursor-zoom-in items-center justify-center bg-[#0a1a2f]/0 opacity-0 transition-all duration-300 group-hover:bg-[#0a1a2f]/25 group-hover:opacity-100 group-focus-within:opacity-100"
            aria-label={`View project image ${index + 1} full size`}
          >
            <span
              className={`inline-flex items-center gap-1.5 rounded-full bg-white font-bold uppercase tracking-wider text-[#0a1a2f] shadow-lg ${
                compact ? "px-3 py-1.5 text-[10px]" : "gap-2 px-4 py-2.5 text-xs"
              }`}
            >
              <ZoomIn className={compact ? "h-3 w-3" : "h-4 w-4"} />
              View full image
            </span>
          </button>
        )}
      </div>
    </article>
  );
}

export default function ProjectImagesCarousel({
  images,
  carouselKey = "project-images",
  autoPlay = true,
  autoPlayInterval = AUTOPLAY_MS,
  limit,
  enableLightbox = false,
  lightboxTitle = "Our Projects",
  compact = false,
}) {
  const slides = limit ? images.slice(0, limit) : images;
  const [api, setApi] = useState(null);
  const [paused, setPaused] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    if (!api || !autoPlay || slides.length <= 1 || paused) return undefined;

    const timer = window.setInterval(() => {
      api.scrollNext();
    }, autoPlayInterval);

    return () => window.clearInterval(timer);
  }, [api, autoPlay, autoPlayInterval, paused, slides.length, carouselKey]);

  if (!slides.length) return null;

  return (
    <>
      <Carousel
        key={carouselKey}
        setApi={setApi}
        opts={{
          align: "start",
          loop: slides.length > 2,
        }}
        className="w-full"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocusCapture={() => setPaused(true)}
        onBlurCapture={() => setPaused(false)}
      >
        <CarouselContent className="-ml-3 md:-ml-4">
          {slides.map((src, index) => (
            <CarouselItem
              key={`${src}-${index}`}
              className="basis-full pl-3 sm:basis-1/2 md:pl-4"
            >
              <ProjectCarouselSlide
                src={src}
                index={index}
                onZoom={setLightboxIndex}
                enableLightbox={enableLightbox}
                compact={compact}
              />
            </CarouselItem>
          ))}
        </CarouselContent>

        {slides.length > 1 && (
          <>
            <CarouselPrevious className={prevClassName} />
            <CarouselNext className={nextClassName} />
          </>
        )}
      </Carousel>

      {enableLightbox && lightboxIndex !== null && (
        <ProjectImagesLightbox
          images={slides}
          activeIndex={lightboxIndex}
          title={lightboxTitle}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}
    </>
  );
}
